import React, {useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  Platform,
  StyleSheet,
  View,
} from 'react-native';
import {ThemeProvider} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import ItemSeparator from '../../components/ItemSeparator';
import LoadingImg from '../../components/LoadingImg';
import RangeSortFilterPanel from '../../components/RangeSortFilterPanel';
import RightSideFilters from '../../components/RightSideFilters';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {
  ScreenTypes,
  useWebScreenDimensions,
} from '../../hooks/useWebScreenDimensions';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import rewardsTheme from '../../theme/rewardsTheme';
import {OrderLine} from '../../transport/apiServiceResponse';
import LastMinuteOptionTileCard from '../cart/components/LastMinuteOptionTileCard';

const RangeProductListScreen = observer(() => {
  const {rangeStore} = useStores();
  const listRef = useRef(null);
  const {isWebDesktopScreen, screenType} = useWebScreenDimensions();

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isRangeLinesLoading, setIsRangeLinesLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setIsRangeLinesLoading(true);
      await rangeStore.loadRangeLines();
      setIsRangeLinesLoading(false);
    }
    load();
  }, [
    rangeStore.selectedSubDepartment,
    rangeStore.selectedSortOption,
    rangeStore.selectedFilterOptions,
    rangeStore,
  ]);

  let numColumns = 2;

  switch (screenType) {
    case ScreenTypes.mobile:
      numColumns = 2;
      break;
    case ScreenTypes.desktop:
      numColumns = 4;
      break;
    case ScreenTypes.desktopLarge:
      numColumns = 6;
      break;
  }

  const productTileFullHorizontalWrapperWidthStyle = StyleSheet.create({
    main: {
      flex: 1 / numColumns,
    },
  });

  // No longer delay the entry based on index - as we're paginating the list,
  // we end up waiting ages for the new stuff to slide in.
  const renderItem: ListRenderItem<OrderLine> = ({item}) => (
    <View
      style={[productTileFullHorizontalWrapperWidthStyle.main]}
      testID="range-product-flatlist-item-wrapper">
      <LastMinuteOptionTileCard item={item} fullWidth />
    </View>
  );

  return (
    // The "ProductTileFullHorizontal"s have an extra 8 padding
    <RightSideFilters menuWidthProportion={0.7} menu={<RangeSortFilterPanel />}>
      <ContentView
        testID="shop-product-list-screen"
        style={{paddingHorizontal: 8}}>
        {isRangeLinesLoading ? (
          <LoadingImg />
        ) : (
          <>
            <ThemeProvider theme={rewardsTheme}>
              <FlatList
                key={`range-product-flatlist-${numColumns}`}
                testID="range-product-flatlist"
                // slice(): [mobx] Out of bounds read: 74 at VirtualizedList (...) at FlatList (...)
                data={rangeStore.departmentLines.slice()}
                renderItem={renderItem}
                columnWrapperStyle={styles.columnWrapperStyle}
                numColumns={numColumns}
                showsVerticalScrollIndicator={isWebDesktopScreen}
                showsHorizontalScrollIndicator={isWebDesktopScreen} // Setting horizontal to false, causes the vertical to also not show
                onEndReachedThreshold={isWebDesktopScreen ? 0.5 : 3}
                onEndReached={() => rangeStore.loadNextPageRangeLines()}
                initialNumToRender={isWebDesktopScreen ? 25 : 15}
                // This causes this on iPhone 14: 1710: [iOS] Filter and sort in Shop and cant select products
                // https://dev.azure.com/lmgdevops/branded_website/_workitems/edit/1710
                // removeClippedSubviews
                keyExtractor={(item, index) => `${item.id}-${index}`}
                ItemSeparatorComponent={ItemSeparator}
                // Handle scroll to top button
                onScrollBeginDrag={() => setShowScrollButton(true)}
                onMomentumScrollBegin={() => setShowScrollButton(true)}
                onMomentumScrollEnd={() => setShowScrollButton(false)}
                onScrollEndDrag={() => setShowScrollButton(false)}
                ref={listRef}
                contentContainerStyle={styles.contentContainer}
                // Needs to use a loading flag and change depending on that state.
                ListEmptyComponent={() =>
                  rangeStore.isLoadingDepartmentLines ? (
                    <LoadingImg />
                  ) : (
                    <View
                      style={{
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        height: 200,
                      }}>
                      <Text
                        style={{
                          fontSize: 16,
                          fontWeight: '500',
                          color: theme.colour.text.heading.primary,
                        }}>
                        NO PRODUCTS FOUND
                      </Text>
                    </View>
                  )
                }
                // Show loading activity if we're paginating
                ListFooterComponent={() =>
                  !!rangeStore.isLoadingNextPage && (
                    <ActivityIndicator
                      size="large"
                      color={theme.colour.activityIndicator.primary}
                    />
                  )
                }
                ListFooterComponentStyle={{marginTop: 24}}
              />
            </ThemeProvider>
            <ScrollToTopButton
              open={showScrollButton}
              callback={() =>
                listRef.current.scrollToIndex({index: 0, animated: true})
              }
            />
          </>
        )}
      </ContentView>
    </RightSideFilters>
  );
});

export default RangeProductListScreen;

const styles = StyleSheet.create({
  columnWrapperStyle: {gap: 8},
  contentContainer: {
    ...Platform.select({
      ios: {},
      web: {},
    }),
  },
});
